import { useState, useEffect, useRef } from 'react';
import { PricingResponse } from '../api/Requests';
import { HttpClient } from '../api/HttpClient';
import { useConfig } from './useConfig';

export function usePricing(authToken: string | null) {
  const config = useConfig();
  const [pricingInfo, setPricingInfo] = useState<PricingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchingRef = useRef<string | null>(null);

  useEffect(() => {
    if (!authToken) {
      setLoading(false);
      return;
    }

    // Prevent duplicate calls for the same authToken (React StrictMode causes double renders in dev)
    if (fetchingRef.current === authToken) {
      return;
    }

    const fetchPricing = async () => {
      try {
        fetchingRef.current = authToken;
        setLoading(true);
        setError(null);
        
        const productId = config.PREMIUM_SUBSCRIPTION_PRODUCT_ID;
        if (!productId) {
          const errorMessage = 'Pricing configuration is missing. Please contact support.';
          setError(errorMessage);
          setLoading(false);
          fetchingRef.current = null;
          return;
        }

        const client = new HttpClient(authToken || undefined);
        const data = await client.get<PricingResponse>(`/pricing?productId=${encodeURIComponent(productId)}`);
        setPricingInfo(data);
      } catch (err) {
        const errorMessage = 'Unable to load pricing information. Please try again later.';
        setError(errorMessage);
        fetchingRef.current = null; // Allow retry on error
      } finally {
        setLoading(false);
      }
    };

    fetchPricing();
  }, [authToken, config]);

  return { 
    pricingInfo, 
    isLoading: loading, 
    pricingError: error 
  };
}

